import { useEffect, useState, ChangeEvent, useContext, memo } from 'react'
import { useParams } from "react-router-dom";
import { format } from 'date-fns'
import Modal from '../../../components/Modal'
import DataGrid, { FormatterProps, EditorProps } from 'react-data-grid'
import { BulkBatchTransferColumn, BulkBatchTransferColumnReadOnly } from '../../../constants/Column'
import UserContext from '../../../contexts/UserContext'
import {
  createMovement,
  updateMovement
} from '../../../states/PicklistAction'
import { updateActualBatchNo } from '../../../apis/order'
import { MdOutlineCancel } from 'react-icons/md'
import { IoMdAddCircle } from 'react-icons/io'
import RemoveLotEditor from '../../../components/RemoveLotEditor';
import NotFound404 from "../../../components/NotFound404"
import { Department, DgtPdOrderPicklistMovement } from '../../../models';
import MovementContext from '../../../contexts/MovementContext';
import { OrderPicklistMovement, ProductionOrder } from '../../../components/types';
import PicklistContext from '../../../contexts/PicklistContext';
import OrderContext from '../../../contexts/OrderContext';

interface EditBatchNoProps {
  batchNumbers: string[]
  onUpdateCompleted: (updatedOrder? : ProductionOrder) => void
  onSkiped: () => void
  isEmptyLot?: boolean
}

interface Params {
  id: string
}

export interface BulkBatch {
  code: string
  description?: string
  currentBatchNo: string
  newBatchNo: string
  fillToday: boolean
  approvedBy?: string
  approvedDatetime?: number
  checkedBy?: string
  checkedDatetime?: number
  deleted?: boolean
  shelfLifeMonth?: number
  fgCode?: string
}

const EditBatchNo = ({ batchNumbers, onUpdateCompleted, onSkiped, isEmptyLot = false } : EditBatchNoProps) => {
  const { id } = useParams<Params>()

  const [editedBatchNo, setEditedBatchNo] = useState<BulkBatch[]>([])
  const [checkedAllPreFillToday, setCheckedAllPreFillToday] = useState<boolean>(false)

  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false)
  const [saving, setSaving] = useState<boolean>(false)

  const movementContext = useContext(MovementContext)
  const picklistContext = useContext(PicklistContext)
  const orderContext = useContext(OrderContext)
  const orderFromParent = orderContext.order as unknown as ProductionOrder
  
  const userCon = useContext(UserContext)
  const user : any = userCon.user
  const username = user?.name || user?.givenName || user?.username || ''
  const readOnly = userCon.readOnly
  const isAdmin = user.group === 'Admins'
  const isPD = user.department === Department.PRODUCTION

  const onCheckedAllPreFillToday = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const today = format(new Date(), 'ddMM')
      setEditedBatchNo(batchNumbers.map(bn => {
        const batch = editedBatchNo.find(b => b.currentBatchNo === bn)
        return {
          "currentBatchNo": bn,
          "newBatchNo": bn.includes('_') ? `${today}${bn.replace(/_/ig, '')}` : bn,
          "fillToday": true,
          "description": batch!.description || '',
          "checkedBy": batch!.checkedBy || username,
          "checkedDatetime": batch!.checkedDatetime || 0,
          "approvedDatetime": batch!.approvedDatetime || 0,
          "approvedBy": batch!.approvedBy || '',
          "code": batch!.code,
          "fgCode": orderFromParent.materialCode,
          "shelfLifeMonth": orderFromParent.shelfLifeMonth || (Number(orderFromParent.shelfLife) || 0) * 12
        }
      }))
    } else {
      setEditedBatchNo(batchNumbers.map(bn => {
        const batch = editedBatchNo.find(b => b.currentBatchNo === bn)
        return { "currentBatchNo": bn,
          "newBatchNo": bn, 
          "fillToday": false,
          "description": batch!.description || '', 
          "checkedBy": batch!.checkedBy || username,
          "checkedDatetime": batch!.checkedDatetime || 0,
          "approvedDatetime": batch!.approvedDatetime || 0,
          "approvedBy": batch!.approvedBy || '',
          "code": batch!.code,
          "fgCode": orderFromParent.materialCode,
          "shelfLifeMonth": orderFromParent.shelfLifeMonth || (Number(orderFromParent.shelfLife) || 0) * 12
        }
      }))
    }
    setCheckedAllPreFillToday(e.target.checked)
  }

  useEffect(() => {
    const fetchBulk = async () => {
      const bulkMovements = movementContext ? movementContext.movements
        .filter(mv => mv.materialType === 'BULK') : []
      const bulkItems = picklistContext ? picklistContext.items
        .filter(item => item.materialType === 'BULK') : []
      
      const defaultMatCode = bulkItems.length > 0 ? bulkItems[0].materialCode : ''
      const defaultMatDesc = bulkItems.length > 0 ? bulkItems[0].materialDescription : ''

      const actualLots = isEmptyLot ? [...batchNumbers, '-'] : batchNumbers
      if (bulkMovements.length > 0) {
        setEditedBatchNo(actualLots.map(bn => {
          const mv = bulkMovements.find(m => m.batchLotNo === bn || m.batchLotNo?.replace('____', '') === bn.substring(bn.length - 6))
          return { 
            currentBatchNo: bn, 
            newBatchNo: bn, 
            fillToday: false,
            description: defaultMatDesc,
            code: defaultMatCode,
            approvedBy: mv ? mv.approvedBy || '' : '',
            approvedDatetime: mv ? mv.approvedDateTime || 0: 0,
            checkedBy: mv ? mv.checkedBy || username : username,
            checkedDatetime: mv ? mv.checkedDateTime || 0: 0,
            fgCode: orderFromParent.materialCode,
            shelfLifeMonth: orderFromParent.shelfLifeMonth || (Number(orderFromParent.shelfLife) || 0) * 12
          }
        }))
      } else {
        setEditedBatchNo(actualLots.map(bn => {
          return { 
            currentBatchNo: bn, 
            newBatchNo: bn, 
            fillToday: false,
            description: defaultMatDesc,
            checkedBy: username,
            code: defaultMatCode,
            fgCode: orderFromParent.materialCode,
            shelfLifeMonth: orderFromParent.shelfLifeMonth || (Number(orderFromParent.shelfLife) || 0) * 12
          }
        }))
      }
    }
    fetchBulk()
    
  }, [ movementContext, picklistContext, batchNumbers, isEmptyLot, username, orderFromParent.materialCode, orderFromParent.shelfLife, orderFromParent.shelfLifeMonth ])

  const updateBatchNo = async () => {
    setSaving(true)

    let orderUpdated = undefined
    if (editedBatchNo.filter(b => b.currentBatchNo !== b.newBatchNo || b.deleted).length > 0) {
      orderUpdated = await updateActualBatchNo(
        id, 
        editedBatchNo.filter(bn => !bn.deleted).map(bn => bn.newBatchNo.trim())
      )
      console.log('updated', orderUpdated)
    }
    
    const checkedOrApproved = editedBatchNo.filter(b => b.checkedDatetime || b.approvedDatetime)
    if (checkedOrApproved.length > 0) {
      let actions = []
      for (const batch of checkedOrApproved) {
        const skFullBatch = `${batch.code}#${batch.currentBatchNo === '-' ? batch.newBatchNo : batch.currentBatchNo}#261`
        const skPreBatch = `${batch.code}#${batch.currentBatchNo.replace(/^.{4}/g, '____')}#261`

        const mvs : OrderPicklistMovement[] = movementContext ? movementContext.movements
          .filter(mv => mv.materialType === 'BULK') : []
        const existMovements = [
          ...mvs.filter(mv => mv.sk === skFullBatch || mv.batchLotNo === batch.currentBatchNo),
          ...mvs.filter(mv => mv.sk === skPreBatch || mv.batchLotNo === batch.currentBatchNo)
        ]

        if (existMovements.length > 0) {
          let payload: any = {
            'batchLotNo': batch.newBatchNo
          }
          if (batch.approvedDatetime) {
            payload = {
              ...payload,
              'approvedDateTime': batch.approvedDatetime,
              'approvedBy': batch.approvedBy
            }
          }
          if (batch.checkedDatetime) {
            payload = {
              ...payload,
              'checkedDateTime': batch.checkedDatetime,
              'checkedBy': batch.checkedBy
            }
          }

          actions.push(updateMovement(id, existMovements[0].sk, payload, existMovements[0]._version!))
        } else {
          actions.push(createMovement(id, skFullBatch, batch))
        }
      }
      const results = await Promise.allSettled(actions)
      const updatedMovements = (results.filter(res => res.status === 'fulfilled') as PromiseFulfilledResult<DgtPdOrderPicklistMovement>[])
        .map(res => res.value)
      movementContext?.mutateMovements(updatedMovements)
    }
    
    setShowConfirmDialog(false)
    setSaving(false)
    onUpdateCompleted(orderUpdated)
  }
  
  const isDisabledSubmit = editedBatchNo.map(btn => btn.newBatchNo).filter(newBtn => !newBtn).length > 0
  const changedBatches = editedBatchNo.filter(
    b => b.currentBatchNo !== b.newBatchNo || b.checkedDatetime || b.approvedDatetime || b.deleted)
  const isNoChanged = changedBatches.length === 0

  const isInvalidBatchNo = editedBatchNo
    .filter(bat => bat.newBatchNo !== '-')
    .filter(bat => bat.newBatchNo.length !== 10 || /\s/.test(bat.newBatchNo)).length > 0 

  const isNotMatchedLot = editedBatchNo.filter(
    bat => bat.currentBatchNo !== 'AAAAAAAAAA' && bat.currentBatchNo !== '-' && bat.currentBatchNo.slice(-6) !== bat.newBatchNo.slice(-6)).length > 0

  let columns = Array.from(readOnly ? BulkBatchTransferColumnReadOnly : BulkBatchTransferColumn)
  if (id.startsWith('8')) {
    columns.splice(3, 3);
    // note : dont need to sign in case order bulk (bulk with soap base ?)
  }
  if (!readOnly && isAdmin && editedBatchNo.length > 1) {
    columns.push({
      key: 'delete', name: 'ลบ', width: 80, headerCellClass: 'text-center',
      cellClass: 'flex items-center justify-center cursor-pointer',
      formatter: (props: FormatterProps<BulkBatch>) => {
        return props.row.deleted ? 
          <IoMdAddCircle size={20} className='text-green-700 ' /> : 
          <MdOutlineCancel color='red' size={20}/>
      },
      editable: true,
      editor : (props: EditorProps<BulkBatch>) => {
        return <RemoveLotEditor props={props} />
      },
      editorOptions: {
        editOnClick: true
      }
    })
  }

  return isPD ? <div className='mt-1 md:container md:mx-auto xl:px-36'>
    <div className='flex justify-between'>
      <h6>ทั้งหมด {batchNumbers.length} Batch(s)</h6>
      <h6 className='font-bold '>{editedBatchNo.length > 0 ? editedBatchNo[0].description : ''}</h6>
      <div>
        <input className='mr-2' type="checkbox" value="" id="flexCheckDefault"
          onChange={onCheckedAllPreFillToday} checked={checkedAllPreFillToday} disabled={readOnly}/>
        <label className="inline-block text-gray-800" htmlFor="flexCheckDefault">เติมวันนี้ทั้งหมด</label>
      </div>
    </div>
    <DataGrid
      className='mt-2 '
      style={{ height: '100%' }}
      columns={columns}
      rows={editedBatchNo}
      onRowsChange={setEditedBatchNo}
      rowHeight={48}
      rowClass={row => row.deleted ? 'deprecated-item-row' : ''}
    />
    <div className='flex justify-end '>
      <small className='py-2 italic text-muted'>หมายเหตุ : กรุณากรอกเลข Batch Lot 10 หลัก ของ Bulk ที่รับมาผลิต ก่อนกดลงชื่อ</small>
    </div>
    <div className="float-right py-3">
      {isInvalidBatchNo ? <small className='mr-3 italic text-red-500 '>กรอก Lot No ต้องเป็นตัวเลข 10 หลักเท่านั้น และห้ามมีค่าว่าง</small> : null}
      {isNotMatchedLot ? <small className='mr-3 italic text-red-500 '>กรุณากรอก Lot No เลขลงท้าย 6 หลักให้ตรงกับที่ระบบเตรียมไว้</small> : null}
      <button className="inline-flex px-4 py-2 mr-2 text-sm font-medium text-white bg-gray-500 rounded hover:bg-gray-600"
        onClick={onSkiped}
        disabled={readOnly}>ข้าม</button>
      <button className="inline-flex px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded disabled:opacity-50 hover:bg-blue-700"
        onClick={() => setShowConfirmDialog(true)}
        disabled={isDisabledSubmit || isNoChanged || readOnly || isInvalidBatchNo || isNotMatchedLot}>บันทึก</button>
    </div>
    {showConfirmDialog ? <Modal
			show={showConfirmDialog}
			title='แก้ไข Batch No / ยืนยันรับจ่าย BULK'
			cancelText='ยกเลิก'
      okText='ยืนยัน'
      okAction={updateBatchNo}
			onHide={() => setShowConfirmDialog(false)}
      loading={saving}
		>
      <>
        <h6>ผู้ร่วมงานยืนยันที่จะแก้ไข Batch No ดังนี้</h6>
        <div className="mt-4">
          {changedBatches.filter(b => b.currentBatchNo !== b.newBatchNo).map( (batch, idx) =>
            <h6 key={idx}>{`${idx+1}. Batch ${batch.currentBatchNo} เป็น ${batch.newBatchNo}`}</h6>
          )}
        </div>
        <h6 className='mt-3 '>ผู้ร่วมงานยืนยันการรับจ่าย BULK</h6>
        {changedBatches.filter(b => b.checkedDatetime || b.approvedDatetime).map( (batch, idx) =>
            <div key={idx}>
              <h6>{`${idx+1}. Batch ${batch.newBatchNo}`}</h6>
              <h6>{` - จ่ายโดย ${batch.approvedBy} / รับโดย ${batch.checkedBy}`}</h6>
            </div>
          )}
      </>
    </Modal> : null}
  </div> : <NotFound404 
    title="ไม่มีสิทธิ์ในการใช้งาน"
    subTitle=""
  />
}

export default memo(EditBatchNo)